import type { Dispatch } from 'redux';
import { Platform } from 'react-native';
import {
  OWNER_SETUP_FEATURE_VERSION,
  markOwnerSetupEligible,
  markOwnerSetupSessionBootstrapError,
  markOwnerSetupSessionReady,
} from '../../redux/ownerActivationSlice';
import type { OwnerSetupSessionHandoffMarker } from '../../services/ownerSetupSessionHandoff';
import ownerActivationAnalytics from '../../services/ownerActivationAnalytics';
import type { OwnerActivationAnalyticsPlatform } from '../../services/ownerActivationAnalyticsTypes';

/**
 * Durable owner-conversion handoff for BusinessDetails success (US1, T030).
 *
 * Ordering is the contract:
 *  1. batch-write fresh employee tokens + transient handoff marker;
 *  2. dispatch owner-bound *pending* feature-v1 eligibility;
 *  3. await persistor.flush() so eligibility survives process death;
 *  4. retrieve the employee profile and apply it to Redux;
 *  5. run Login-parity session preload (employee locations + owner
 *     backoffice login/preload; failures are isolated);
 *  6. expose auth tokens (flips Auth→App);
 *  7. mark session ready, flush, and delete the marker.
 *
 * Process death between 1 and 3 is recovered from the marker; between 3 and 7
 * from persisted pending eligibility. Owner conversion is never repeated.
 */

export type OwnerSetupCompletionOutcome =
  | { kind: 'ready' }
  | { kind: 'requires_reauthentication' }
  | { kind: 'employee_missing' }
  /** Transient profile/preload failure: pending eligibility retained. */
  | { kind: 'profile_error' };

export interface OwnerSetupCompletionDeps {
  dispatch: Dispatch;
  /** Persist both tokens (AsyncStorage batch write). */
  persistTokens(accessToken: string, refreshToken: string): Promise<void>;
  writeHandoffMarker(marker: OwnerSetupSessionHandoffMarker): Promise<void>;
  clearHandoffMarker(): Promise<void>;
  /** Singleton persistor flush. */
  flushPersistor(): Promise<unknown>;
  /** GET employee profile with the fresh access token. */
  fetchEmployeeProfile(accessToken: string): Promise<any>;
  /**
   * Apply profile payload to Redux (employee + business info). Returns the
   * employee id used for session preload.
   */
  applyEmployeeProfile(profileResponse: any): string;
  /**
   * Login-parity preload with explicit fresh credentials:
   * employee locations, then owner backoffice auto-login + owner preload.
   * Failures must be tolerated (throwing is treated as non-fatal).
   */
  preloadSession(
    accessToken: string,
    employeeId: string,
    ownerId: string,
  ): Promise<void>;
  /** Expose tokens to the auth context(s); flips the Auth/App switch. */
  setAuthTokens(accessToken: string, refreshToken: string): void;
  now(): string;
}

export async function completeOwnerSetupHandoff(
  deps: OwnerSetupCompletionDeps,
  params: { ownerId: string; accessToken: string; refreshToken: string },
): Promise<OwnerSetupCompletionOutcome> {
  const { ownerId, accessToken, refreshToken } = params;

  // 1. Tokens + marker batch-write (durable before anything observable).
  await deps.persistTokens(accessToken, refreshToken);
  await deps.writeHandoffMarker({
    ownerKey: ownerId,
    featureVersion: OWNER_SETUP_FEATURE_VERSION,
    stage: 'tokens_written',
  });

  // 2-3. Owner-bound pending eligibility, flushed before profile retrieval.
  deps.dispatch(
    markOwnerSetupEligible({
      ownerKey: ownerId,
      markedAt: deps.now(),
      source: 'business_setup_completion',
      featureVersion: OWNER_SETUP_FEATURE_VERSION,
      sessionBootstrap: 'pending',
    }),
  );
  try {
    const platform: OwnerActivationAnalyticsPlatform =
      Platform.OS === 'ios'
        ? 'ios'
        : Platform.OS === 'android'
          ? 'android'
          : 'web';
    ownerActivationAnalytics.track('owner_setup_eligible', {
      onboarding_version: 1,
      platform,
      source: 'business_setup_completion',
    });
  } catch {
    // Analytics must never break the durable handoff.
  }
  await deps.flushPersistor();
  await deps.writeHandoffMarker({
    ownerKey: ownerId,
    featureVersion: OWNER_SETUP_FEATURE_VERSION,
    stage: 'eligibility_flushed',
  });

  // 4. Employee profile with the fresh token.
  let profileResponse: any;
  try {
    profileResponse = await deps.fetchEmployeeProfile(accessToken);
  } catch (error) {
    console.log('owner setup profile retrieval failed', error);
    deps.dispatch(markOwnerSetupSessionBootstrapError({ error: 'network' }));
    return { kind: 'profile_error' };
  }

  if (profileResponse?.data?.status !== 200) {
    if (profileResponse?.data?.message === 'Employee does not exist') {
      return { kind: 'employee_missing' };
    }
    deps.dispatch(
      markOwnerSetupSessionBootstrapError({
        error: 'employee_profile_unavailable',
      }),
    );
    return { kind: 'profile_error' };
  }
  if (profileResponse?.data?.data?.bp_employee_requires_reauthentication) {
    return { kind: 'requires_reauthentication' };
  }
  if (profileResponse?.data?.message === 'Employee does not exist') {
    return { kind: 'employee_missing' };
  }

  const employeeId = deps.applyEmployeeProfile(profileResponse);

  // 5. Login-parity preload (employee locations + owner backoffice); non-fatal.
  try {
    await deps.preloadSession(accessToken, employeeId, ownerId);
  } catch (error) {
    console.log('owner setup preload skipped', error);
  }

  // 6. Only now flip Auth→App: eligibility is already durable, so the app
  // routes into the wizard, not Home.
  deps.setAuthTokens(accessToken, refreshToken);

  // 7. Session ready; marker no longer needed.
  deps.dispatch(markOwnerSetupSessionReady());
  await deps.flushPersistor();
  await deps.clearHandoffMarker();
  return { kind: 'ready' };
}
